<template>
  <div class="order-list-item">
    <div class="tit">
      <div class="time">{{ item.pay_time }}</div>
      <div class="status">
        <span class="group-tag" v-if="item.is_group">拼单</span>
        <span>{{ item.state_text }}</span>
      </div>
    </div>

    <!-- 拼单状态提示 -->
    <div class="group-tip" v-if="isWaitingGroup">
      <span>💰 已支付，等待好友参团，满 {{ item.group_total_num || 2 }} 人即发货；未成团可随时申请退款（系统自动原路退回）</span>
    </div>
    <div class="group-tip fail" v-if="isGroupFailed">
      <span>😔 拼团未成功，点击「申请退款」系统将自动原路退款，无需等待客服审核</span>
    </div>

    <!-- 待支付订单：显示剩余支付时间倒计时 -->
    <div class="countdown" v-if="isPendingPayment">
      <span class="cd-label">剩余支付时间</span>
      <span class="cd-time" :class="{ danger: remainingSeconds <= 60 }">{{ remainingText }}</span>
    </div>

    <div class="list">
      <div class="list-item" v-for="elem in item.goods" :key="elem.goods_id">
        <div class="goods-img">
          <img :src="elem.goods_image" alt="">
        </div>
        <div class="goods-content">
          <div class="goods-name text-ellipsis-2">{{ elem.goods_name }}</div>
          <div class="goods-spec" v-if="elem.spec_text">{{ elem.spec_text }}</div>
        </div>
        <div class="goods-trade">
          <p>¥ {{ elem.goods_price }}</p>
          <p>x {{ elem.total_num }}</p>
        </div>
      </div>
    </div>
    <div class="total"> 共{{ item.goods_total_num }}件商品，总金额 ¥{{ item.total_price }} </div>

    <!-- 退款信息提示 -->
    <div class="refund-tip" v-if="item.refund">
      <span v-if="item.refund.status === 0">⏳ 退款处理中：{{ item.refund.reason }}</span>
      <span v-else-if="item.refund.status === 1">✅ 已退款：{{ item.refund.reply || '退款已完成' }}</span>
      <span v-else-if="item.refund.status === 2">❌ 退款被拒绝：{{ item.refund.reply || '未通过审核' }}</span>
    </div>

    <!-- 订单操作按钮 -->
    <div class="actions">
      <button v-if="isPendingPayment" class="action-btn pay" @click="goPay">去支付</button>
      <button v-if="isWaitingGroup" class="action-btn pay" @click="goShare">去分享</button>
      <button v-if="canRefund" class="action-btn danger" @click="$emit('refund', item)">申请退款</button>
      <button v-if="isReceiving" class="action-btn confirm" @click="$emit('confirm', item)">确认收货</button>
      <button v-if="isCompleted && !isCommented" class="action-btn comment" @click="$emit('comment', item)">评价</button>
      <button class="action-btn service" @click="contactService">联系客服</button>
    </div>
  </div>
</template>

<script>
// 待支付订单超时时间（与后端一致）：15 分钟
const PAY_TIMEOUT_MINUTES = 15

import { Toast } from 'vant'

export default {
  name: 'OrderListItem',
  props: {
    // 订单数据
    item: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      remainingSeconds: 0,
      timer: null
    }
  },
  computed: {
    isPendingPayment () {
      return this.item.state_text === '待支付'
    },
    // 拼单已支付待成团
    isWaitingGroup () {
      return this.item.state_text === '待成团'
    },
    // 拼团失败
    isGroupFailed () {
      return this.item.state_text === '拼团失败'
    },
    canRefund () {
      // 普通订单待发货/待收货可退；拼单待成团（已支付）可退；拼团失败订单可退
      return this.item.state_text === '待发货' ||
        this.item.state_text === '待收货' ||
        this.item.state_text === '待成团' ||
        this.item.state_text === '拼团失败'
    },
    isReceiving () {
      return this.item.state_text === '待收货'
    },
    isCompleted () {
      return this.item.state_text === '已完成'
    },
    // 是否已全部评价（已评价完成的订单不再显示「评价」按钮）
    isCommented () {
      return !!this.item.is_commented
    },
    remainingText () {
      const s = Math.max(0, this.remainingSeconds)
      const mm = String(Math.floor(s / 60)).padStart(2, '0')
      const ss = String(s % 60).padStart(2, '0')
      return `${mm}:${ss}`
    }
  },
  created () {
    if (this.isPendingPayment) {
      this.startCountdown()
    }
  },
  beforeDestroy () {
    this.clearTimer()
  },
  methods: {
    clearTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    // 计算剩余秒数
    getRemainingSeconds () {
      const createTime = this.item.create_time || ''
      // "YYYY-MM-DD HH:MM:SS" -> "YYYY-MM-DDTHH:MM:SS"（兼容 Safari）
      const expireMs = new Date(createTime.replace(' ', 'T')).getTime() + PAY_TIMEOUT_MINUTES * 60 * 1000
      return Math.floor((expireMs - Date.now()) / 1000)
    },
    startCountdown () {
      this.remainingSeconds = this.getRemainingSeconds()
      this.timer = setInterval(() => {
        this.remainingSeconds = this.getRemainingSeconds()
        if (this.remainingSeconds <= 0) {
          this.clearTimer()
          // 通知父组件刷新（后端会删除超时订单）
          this.$emit('expired', this.item.order_id)
        }
      }, 1000)
    },
    // 跳转到收银台继续支付
    goPay () {
      this.$router.push({
        path: '/payment',
        query: {
          orderId: this.item.order_id,
          orderNo: this.item.order_no,
          amount: this.item.total_price
        }
      })
    },
    // 待成团订单：跳转拼单分享页邀请好友
    goShare () {
      if (this.item.group_no) {
        this.$router.push(`/group?groupNo=${this.item.group_no}`)
      } else {
        Toast('拼单信息不存在，请刷新后重试')
      }
    },
    // 联系客服（携带订单号）
    contactService () {
      this.$router.push({
        path: '/chat',
        query: { orderNo: this.item.order_no || '' }
      })
    }
  }
}
</script>

<style lang="less" scoped>
.order-list-item {
  margin: 10px auto;
  width: 94%;
  padding: 15px;
  background-color: #fff;
  box-shadow: 0 0.5px 2px 0 rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  color: #333;
  font-size: 13px;

  .tit {
    height: 24px;
    line-height: 24px;
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;

    .status {
      color: #ff6b35;
      display: flex;
      align-items: center;
      gap: 6px;
      .group-tag {
        display: inline-block;
        font-size: 11px;
        color: #fff;
        background: linear-gradient(90deg, #ff6b35, #ff8c42);
        border-radius: 3px;
        padding: 1px 6px;
        line-height: 16px;
      }
    }
  }

  // 拼单状态提示
  .group-tip {
    margin-top: 10px;
    padding: 8px 12px;
    background: #fff7f0;
    border: 1px dashed #ffd2b0;
    border-radius: 6px;
    font-size: 12px;
    color: #b06a00;
    line-height: 1.6;
    &.fail {
      background: #fff1f1;
      border-color: #ffc9c9;
      color: #c0392b;
    }
  }

  .countdown {
    display: flex;
    align-items: center;
    background: #fff7f0;
    border: 1px dashed #ffd2b0;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 10px;
    font-size: 12px;

    .cd-label { color: #b39999; margin-right: 8px; }
    .cd-time {
      color: #ff6b35;
      font-weight: 700;
      font-size: 15px;
      font-variant-numeric: tabular-nums;
      &.danger { color: #f5222d; animation: blink 1s infinite; }
    }
  }

  .list-item {
    display: flex;

    .goods-img {
      width: 90px;
      height: 90px;
      margin: 0 10px 10px 0;

      img {
        width: 100%;
        height: 100%;
      }
    }

    .goods-content {
      flex: 2;
      line-height: 18px;
      max-height: 36px;
      margin-top: 8px;
      .goods-name { max-height: 36px; }
      .goods-spec {
        display: inline-block;
        margin-top: 4px;
        padding: 1px 6px;
        background: #f7f8fa;
        border-radius: 4px;
        font-size: 12px;
        color: #999;
      }
    }

    .goods-trade {
      flex: 1;
      line-height: 18px;
      text-align: right;
      color: #b39999;
      margin-top: 8px;
    }
  }

  .total {
    text-align: right;
  }

  // 退款信息提示
  .refund-tip {
    margin-top: 10px;
    padding: 8px 12px;
    background: #fff8f0;
    border: 1px dashed #ffd2b0;
    border-radius: 6px;
    font-size: 12px;
    color: #b06a00;
    line-height: 1.6;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #f5f5f5;

    .action-btn {
      padding: 6px 18px;
      border: 1px solid #ddd;
      border-radius: 16px;
      font-size: 13px;
      cursor: pointer;
      transition: all 0.2s;
      background: #fff;
      color: #666;

      &:active { transform: scale(0.96); }

      &.pay {
        background: linear-gradient(90deg, #ff6b35, #ff8c42);
        color: #fff;
        border: none;
      }

      &.danger {
        border-color: #ff6b35;
        color: #ff6b35;
      }

      &.confirm {
        background: linear-gradient(90deg, #4caf50, #66bb6a);
        color: #fff;
        border: none;
      }

      &.comment {
        border-color: #ff9211;
        color: #ff9211;
      }

      &.service {
        border-color: #999;
        color: #666;
      }
    }
  }
}

@keyframes blink { 50% { opacity: 0.4; } }
</style>

